/**
 * +atmos: the distant world (replaces the camera-following mountain cards): real low-poly ranges
 * ringing the zone in world space, so parallax works as you walk and when the camera pitches low.
 * Foothills climb straight out of the terrain margin (forested lower slopes with a band of cheap
 * conifers), then ridged ranges rise to snowy peaks ~1 km out; a pass opens toward the Tower.
 * Faceted toon shading from the sun, a sunlit rim, and aerial perspective: bluer and paler with
 * distance, crisper on the high peaks, misty at the feet. One mesh + one instanced tree mesh.
 */
import * as THREE from 'three';
import { U } from '../../style';
import { ATMOS } from './uniforms';
import { mountainK, passK } from '../edge'; // +edge

export interface FarWorldOpts {
  /** The terrain grid's extent (sim yd): the ranges start just inside its edge. */
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** Terrain height at a sim point (clamped into the grid by the caller's sampler). */
  heightAt(x: number, y: number): number;
  /** Sim direction from the map centre toward the Tower (a pass opens there), or null. */
  towerDir: { x: number; y: number } | null;
  /** Rock / forest / snow / haze base colours. */
  colors?: { rock: number; forest: number; snow: number; grass: number };
  /** Conifer geometry for the foothill forest (unit-ish height ~10 yd), or null for none. */
  tree: THREE.BufferGeometry | null;
}

export interface FarWorld {
  group: THREE.Group;
  /** Height of the far ground at a sim point (for anchoring the Tower). */
  heightAt(x: number, y: number): number;
  /** Multiplier for the body colour (time of day). */
  uLand: { value: THREE.Color };
  dispose(): void;
}

/** How far the ranges reach beyond the terrain grid (yd). */
export const RANGE_REACH = 1050;

function hash(x: number, y: number): number {
  const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return s - Math.floor(s);
}
function vn(x: number, y: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = hash(xi, yi);
  const b = hash(xi + 1, yi);
  const c = hash(xi, yi + 1);
  const d = hash(xi + 1, yi + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}
const sm = (e0: number, e1: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

export function buildFarWorld(o: FarWorldOpts): FarWorld {
  const INSET = 24;
  const rx0 = o.x0 + INSET;
  const ry0 = o.y0 + INSET;
  const rx1 = o.x1 - INSET;
  const ry1 = o.y1 - INSET;
  const cx = (o.x0 + o.x1) / 2;
  const cy = (o.y0 + o.y1) / 2;
  const tAng = o.towerDir ? Math.atan2(o.towerDir.y, o.towerDir.x) : null;
  /** Distance outside the inner rectangle (negative inside). */
  const outside = (x: number, y: number): number => {
    const dx = Math.max(rx0 - x, 0, x - rx1);
    const dy = Math.max(ry0 - y, 0, y - ry1);
    if (dx === 0 && dy === 0) return -Math.min(x - rx0, rx1 - x, y - ry0, ry1 - y);
    return Math.hypot(dx, dy);
  };
  const edgeH = (x: number, y: number): number => o.heightAt(Math.min(o.x1 - 1, Math.max(o.x0 + 1, x)), Math.min(o.y1 - 1, Math.max(o.y0 + 1, y)));
  const H = (x: number, y: number): number => {
    const d = outside(x, y);
    const base = edgeH(x, y) - 0.8;
    if (d <= 0) return base - 2;
    // Foothills: hummocks climbing out of the margin.
    const foot = d * 0.1 * sm(0, 60, d) + 14 * sm(20, 260, d) * (vn(x / 70 + 3, y / 70 - 2) - 0.25);
    // Ranges: ridged noise, taller with distance; a pass toward the Tower.
    const r1 = 1 - Math.abs(vn(x / 230 + 11, y / 230 + 7) * 2 - 1);
    const r2 = 1 - Math.abs(vn(x / 95 - 4, y / 95 + 13) * 2 - 1);
    const n3 = vn(x / 40, y / 40);
    let amp = 24 + 150 * sm(120, 900, d);
    if (tAng !== null) {
      const da = Math.atan2(Math.sin(Math.atan2(y - cy, x - cx) - tAng), Math.cos(Math.atan2(y - cy, x - cx) - tAng));
      amp *= 1 - 0.72 * Math.exp(-((da / 0.16) ** 2));
    }
    const shape = 0.58 * r1 * r1 + 0.3 * r2 * r2 + 0.12 * n3;
    // +edge: the zone's edge mountains (outdoor/edge.ts) keep climbing out of the margin, so the
    // ranges continue their flanks instead of starting from a shelf; forested stretches roll on.
    const lift = 55 * Math.pow(sm(0, 220, d), 0.8) * mountainK(x, y) * (1 - 0.65 * passK({ cx, cy, towerAng: tAng }, x, y)) * (0.7 + 0.6 * r2);
    return base + foot + lift + amp * shape * sm(30, 420, d);
  };

  // ---- the range mesh: a square annulus grid, flat-shaded (faceted), coarser far out ----------
  const R = RANGE_REACH;
  const col = o.colors ?? { rock: 0x7f8ea8, forest: 0x2f5a58, snow: 0xf2f6ff, grass: 0x6f9a5a };
  const cRock = new THREE.Color(col.rock);
  const cForest = new THREE.Color(col.forest);
  const cSnow = new THREE.Color(col.snow);
  const cGrass = new THREE.Color(col.grass);
  // Non-uniform axis samples: 20 yd near the zone, ~60 yd at the outer edge.
  const axis = (a0: number, a1: number): number[] => {
    const out: number[] = [];
    let v = a0 - R;
    while (v < a1 + R) {
      out.push(v);
      const d = Math.max(a0 - v, v - a1, 0);
      v += d > 0 ? 20 + d * 0.04 : 40;
    }
    out.push(a1 + R);
    return out;
  };
  const xs = axis(rx0, rx1);
  const ys = axis(ry0, ry1);
  const hs = new Float32Array(xs.length * ys.length);
  for (let j = 0; j < ys.length; j++) for (let i = 0; i < xs.length; i++) hs[j * xs.length + i] = H(xs[i]!, ys[j]!);
  const pos: number[] = [];
  const cols: number[] = [];
  const tmp = new THREE.Color();
  const colorAt = (x: number, y: number, h: number, slope: number): THREE.Color => {
    const d = outside(x, y);
    const rel = h - edgeH(x, y);
    const forest = sm(0.75, 0.45, slope) * (1 - sm(70, 140, rel)) * (0.6 + 0.4 * vn(x / 50, y / 50));
    tmp.copy(cRock).lerp(cGrass, sm(0.6, 0.3, slope) * (1 - sm(40, 120, rel)) * 0.7);
    tmp.lerp(cForest, Math.min(1, forest * 1.3) * sm(0, 30, d));
    tmp.lerp(cSnow, sm(118, 150, rel + 24 * (vn(x / 30, y / 30) - 0.5)) * sm(0.85, 0.5, slope));
    return tmp;
  };
  const tri = (ax: number, ay: number, ah: number, bx: number, by: number, bh: number, qx: number, qy: number, qh: number): void => {
    // Face slope (1 - n.y) for the palette; three.js: x = sim x, y = h, z = -sim y.
    const ux = bx - ax;
    const uy = bh - ah;
    const uz = -(by - ay);
    const vx = qx - ax;
    const vy = qh - ah;
    const vz = -(qy - ay);
    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    const slope = 1 - Math.abs(ny) / (Math.hypot(nx, ny, nz) || 1);
    const c = colorAt((ax + bx + qx) / 3, (ay + by + qy) / 3, (ah + bh + qh) / 3, slope);
    for (const [x, y, h] of [
      [ax, ay, ah],
      [bx, by, bh],
      [qx, qy, qh],
    ] as const) {
      pos.push(x, h, -y);
      cols.push(c.r, c.g, c.b);
    }
  };
  for (let j = 0; j < ys.length - 1; j++)
    for (let i = 0; i < xs.length - 1; i++) {
      const x0 = xs[i]!;
      const x1 = xs[i + 1]!;
      const y0 = ys[j]!;
      const y1 = ys[j + 1]!;
      // Skip cells wholly inside the zone (the real terrain is there).
      if (outside(x0, y0) < -8 && outside(x1, y1) < -8 && outside(x0, y1) < -8 && outside(x1, y0) < -8) continue;
      const h00 = hs[j * xs.length + i]!;
      const h10 = hs[j * xs.length + i + 1]!;
      const h01 = hs[(j + 1) * xs.length + i]!;
      const h11 = hs[(j + 1) * xs.length + i + 1]!;
      // Counter-clockwise seen from above (three.js: -z is sim +y).
      if ((i + j) % 2) {
        tri(x0, y0, h00, x1, y0, h10, x1, y1, h11);
        tri(x0, y0, h00, x1, y1, h11, x0, y1, h01);
      } else {
        tri(x0, y0, h00, x1, y0, h10, x0, y1, h01);
        tri(x1, y0, h10, x1, y1, h11, x0, y1, h01);
      }
    }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3));
  geo.computeBoundingSphere();
  const uLand = { value: new THREE.Color(1, 1, 1) };
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uSun: ATMOS.uSunDir,
      uSunCol: ATMOS.uSunColor,
      uSky: ATMOS.uSkyColor,
      uFog: U.uFogColor,
      uTint: U.uFogTint,
      uShadow: U.uShadowColor,
      uLand: uLand,
    },
    vertexShader: `varying vec3 vW; varying vec3 vC;
      void main(){ vC = color; vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: `uniform vec3 uSun; uniform vec3 uSunCol; uniform vec3 uSky; uniform vec3 uFog; uniform vec3 uTint; uniform vec3 uShadow; uniform vec3 uLand;
      varying vec3 vW; varying vec3 vC;
      void main(){
        vec3 n = normalize(cross(dFdx(vW), dFdy(vW)));
        if (n.y < 0.0) n = -n;
        vec3 sun = normalize(uSun);
        float ndl = dot(n, sun);
        // Toon bands: cool shade, a thin mid, warm light.
        float b1 = smoothstep(0.02, 0.12, ndl);
        float b2 = smoothstep(0.42, 0.5, ndl);
        vec3 shade = uShadow * mix(vec3(0.72, 0.8, 1.0), uSky, 0.35);
        vec3 tone = mix(shade, mix(mix(shade, uSunCol, 0.6), uSunCol * 1.06, b2), b1);
        vec3 c = vC * uLand * tone;
        // Sunlit rim along the crests (grazing view, facing the sun's side).
        vec3 v = normalize(cameraPosition - vW);
        float fres = 1.0 - clamp(dot(n, v), 0.0, 1.0);
        float side = clamp(dot(normalize(n.xz + vec2(1e-4)), normalize(sun.xz + vec2(1e-4))) * 0.5 + 0.5, 0.0, 1.0);
        c += uSunCol * smoothstep(0.55, 0.8, fres) * side * 0.22;
        // Aerial perspective: blue shift, then haze; crisper high up, misty low down.
        float dist = length(vW - cameraPosition);
        float hk = smoothstep(10.0, 160.0, vW.y);
        float a = (1.0 - exp(-dist / 720.0)) * (1.0 - 0.22 * hk) + (1.0 - hk) * 0.12 * smoothstep(200.0, 900.0, dist);
        c *= mix(vec3(1.0), uTint, clamp(dist / 900.0, 0.0, 1.0));
        vec3 haze = mix(uFog, uSky, 0.55);
        c = mix(c, haze, clamp(a, 0.0, 0.9));
        gl_FragColor = vec4(c, 1.0);
      }`,
    vertexColors: true,
  });
  mat.name = 'far-ranges';
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'far-ranges';
  mesh.frustumCulled = false;
  const group = new THREE.Group();
  group.name = 'far-world';
  group.add(mesh);

  // ---- foothill forest: cheap conifers on the gentle lower slopes ---------------------------------
  let trees: THREE.InstancedMesh | null = null;
  let treeMat: THREE.Material | null = null;
  if (o.tree) {
    const pts: { x: number; y: number; h: number; s: number }[] = [];
    let seed = 99991;
    const rnd = (): number => {
      seed = (seed * 16807) % 2147483647;
      return seed / 2147483647;
    };
    const step = 15;
    for (let y = ry0 - 360; y < ry1 + 360; y += step)
      for (let x = rx0 - 360; x < rx1 + 360; x += step) {
        const px = x + rnd() * step;
        const py = y + rnd() * step;
        const d = outside(px, py);
        if (d < 4 || d > 340) continue;
        const h = H(px, py);
        const rel = h - edgeH(px, py);
        const g = (H(px + 4, py) - H(px - 4, py)) ** 2 + (H(px, py + 4) - H(px, py - 4)) ** 2;
        const slope = Math.sqrt(g) / 8;
        const dens = sm(1.1, 0.4, slope) * (1 - sm(60, 120, rel)) * (vn(px / 50, py / 50) * 1.4 - 0.25) * (1 - sm(200, 340, d));
        if (rnd() > dens) continue;
        pts.push({ x: px, y: py, h: h - 0.6, s: 1.3 + rnd() * 0.9 + d * 0.002 });
      }
    // Same lighting model as the ranges (unlit shader + per-instance tint): cheap and consistent.
    const rm = mat.clone();
    rm.uniforms = mat.uniforms;
    rm.vertexShader = `varying vec3 vW; varying vec3 vC;
      void main(){ vC = color; vec4 w = modelMatrix * instanceMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`;
    treeMat = rm;
    trees = new THREE.InstancedMesh(o.tree, rm, pts.length);
    const M4 = new THREE.Matrix4();
    const Q = new THREE.Quaternion();
    const AY = new THREE.Vector3(0, 1, 0);
    const V = new THREE.Vector3();
    const S = new THREE.Vector3();
    pts.forEach((p, i) => {
      M4.compose(V.set(p.x, p.h, -p.y), Q.setFromAxisAngle(AY, hash(p.x, p.y) * 6.283), S.setScalar(p.s));
      trees!.setMatrixAt(i, M4);
    });
    trees.computeBoundingSphere();
    trees.frustumCulled = false;
    trees.name = 'far-foothill-trees';
    group.add(trees);
  }
  return {
    group,
    heightAt: H,
    uLand,
    dispose() {
      group.removeFromParent();
      geo.dispose();
      mat.dispose();
      trees?.dispose();
      treeMat?.dispose();
    },
  };
}
